import React, { useRef, useEffect, useCallback } from 'react';
import config from '../config';
import { lerp, clamp, ease, damp, isMobile, isReduced } from '../hooks/useAnimation';
import '../styles/Orbit.css';

// 六种 AI 成果复利数据
const SATS = [
  { num: '01', tag: 'RESULTS', title: '设计成果', desc: '数据分析、视觉设计、产品方案、产品文档，随时发布。', metric: 'WORK', sub: 'PUBLISH' },
  { num: '02', tag: 'APP', title: '发布应用', desc: '数据 + Skill + 视觉呈现 = 完整应用，自动服务你的客户。', metric: 'APP', sub: 'SERVE' },
  { num: '03', tag: 'ONLINE', title: '公网发布', desc: 'HTML 自动部署到服务器，iPhone 随时可以打开。', metric: 'HTML', sub: 'ONLINE' },
  { num: '04', tag: 'SHARE', title: '微信分享', desc: '把自己设计的作品分享到微信，帮助更多人。', metric: 'WX', sub: 'SHARE' },
  { num: '05', tag: 'PROJECT', title: '工程分享', desc: '一句话获取完整工程文件，共享成果，继续二次开发。', metric: 'ZIP', sub: 'PROJECT' },
  { num: '06', tag: 'PROFILE', title: '个人中心', desc: '聚集你的全部 AI 产出，打造个人品牌。', metric: 'ME', sub: 'BRAND' },
];

// 卫星位置配置
const SAT_BASE_POSITIONS = [
  [-390, -180], [40, -190], [405, -70],
  [335, 185], [-85, 215], [-430, 85]
];

// 揭示顺序
const REVEAL_ORDER = [0, 3, 1, 4, 2, 5];

function Orbit() {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const haloRef = useRef(null);
  const appRef = useRef(null);
  const ctaRef = useRef(null);
  const hintRef = useRef(null);
  const satsRef = useRef([]);
  const orbsRef = useRef([]);
  const labelsRef = useRef([]);

  // 卫星尺寸常量 (与 CSS 中 .sat 保持一致)
  const SAT_SIZE = { w: 276, h: 116 };

  const animRef = useRef({
    orbPs: 0,
    frameRunning: false,
    lastFrameTime: performance.now(),
    satPos: [],
    satStart: [],
    M: { orbit: { top: 0, h: 0 } },
  });

  // 布局卫星位置
  const layoutSats = useCallback(() => {
    const sx = clamp((window.innerWidth - 80) / 1360, 0.6, 1);
    const sy = clamp((window.innerHeight - 170) / 760, 0.82, 1);
    animRef.current.satPos = SAT_BASE_POSITIONS.map(([x, y]) => ({ dx: x * sx, dy: y * sy }));
    animRef.current.satStart = animRef.current.satPos.map(p => ({ dx: p.dx * 0.94, dy: p.dy * 0.94 + 8 }));
  }, []);

  // 测量尺寸
  const measure = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return;
    const rect = section.getBoundingClientRect();
    animRef.current.M.orbit = {
      top: rect.top + window.scrollY,
      h: section.offsetHeight
    };
    layoutSats();
  }, [layoutSats]);

  // 动画帧
  const frame = useCallback((now) => {
    const anim = animRef.current;
    const { M, satPos, satStart } = anim;
    const vh = window.innerHeight;
    const y = window.scrollY;
    const motion = !isMobile() && !isReduced();

    const dt = Math.min(0.05, Math.max(0.001, (now - anim.lastFrameTime) / 1000));
    anim.lastFrameTime = now;

    const orbitTop = M.orbit.top - y;
    const orbitActive = orbitTop < vh && orbitTop + M.orbit.h > 0;

    let needsFrame = false;

    if (orbitActive && motion && window.innerWidth > 900) {
      const P = clamp(-orbitTop / Math.max(M.orbit.h - vh, 1), 0, 1);
      
      // 阻尼平滑
      anim.orbPs = damp(anim.orbPs, P, 7.7, dt);
      if (Math.abs(P - anim.orbPs) > 0.001) needsFrame = true;

      const p = anim.orbPs;

      // 光晕
      if (haloRef.current) {
        const centerIn = ease(clamp((p + 0.06) / 0.14, 0, 1));
        haloRef.current.style.opacity = centerIn * 0.52;
      }

      // 中心 App 砖 - V2 浅色液态玻璃圆角方形
      if (appRef.current) {
        const appIn = ease(clamp((p + 0.06) / 0.14, 0, 1));
        const appScale = lerp(0.92, 1, appIn);
        appRef.current.style.opacity = appIn;
        appRef.current.style.transform = `translate(-50%, -50%) scale(${appScale})`;
      }

      // 卫星动画
      satsRef.current.forEach((sat, i) => {
        if (!sat) return;
        const orb = orbsRef.current[i];
        const label = labelsRef.current[i];
        const pos = satPos[i];
        const start = satStart[i];
        const rank = REVEAL_ORDER.indexOf(i);

        // 卫星入场时机
        const e = ease(clamp((p - 0.14 - rank * 0.03) / 0.18, 0, 1));
        
        // 位置插值 - 减去尺寸的一半实现从中心点偏移 (与 offical.html 一致)
        const dx = lerp(start.dx, pos.dx, e);
        const dy = lerp(start.dy, pos.dy, e);
        
        sat.style.transform = `translate(${dx - SAT_SIZE.w / 2}px, ${dy - SAT_SIZE.h / 2}px)`;

        if (orb) {
          orb.style.opacity = e;
          orb.style.transform = `scale(${lerp(0.975, 1, e)})`;
        }

        if (label) {
          label.style.opacity = e;
          label.style.transform = `translateY(${lerp(8, 0, e)}px)`;
        }
      });

      // CTA 按钮
      if (ctaRef.current) {
        const hintOut = ease(clamp((p - 0.5) / 0.08, 0, 1));
        const ctaIn = ease(clamp((p - 0.57) / 0.12, 0, 1));
        ctaRef.current.style.opacity = ctaIn;
        ctaRef.current.style.transform = `translateX(-50%) translateY(${lerp(12, 0, ctaIn)}px)`;
        ctaRef.current.style.pointerEvents = ctaIn > 0.9 ? 'auto' : 'none';

        // 滚动提示
        if (hintRef.current) {
          hintRef.current.style.opacity = 1 - hintOut;
        }
      }

    } else if (!motion || window.innerWidth <= 900) {
      // 移动端或 reduced motion：静态显示
      if (haloRef.current) haloRef.current.style.opacity = 0;
      if (appRef.current) {
        appRef.current.style.opacity = 1;
        appRef.current.style.transform = 'none';
      }
      satsRef.current.forEach((sat, i) => {
        if (!sat) return;
        sat.style.transform = 'none';
        if (orbsRef.current[i]) {
          orbsRef.current[i].style.opacity = 1;
          orbsRef.current[i].style.transform = 'none';
        }
        if (labelsRef.current[i]) {
          labelsRef.current[i].style.opacity = 1;
          labelsRef.current[i].style.transform = 'none';
        }
      });
      if (ctaRef.current) {
        ctaRef.current.style.opacity = 1;
        ctaRef.current.style.transform = 'none';
        ctaRef.current.style.pointerEvents = 'auto';
      }
      if (hintRef.current) hintRef.current.style.opacity = 0;
    }

    if (motion && needsFrame && !document.hidden) {
      requestAnimationFrame(frame);
    } else {
      anim.frameRunning = false;
    }
  }, []);

  // 启动动画帧
  const startFrame = useCallback(() => {
    const anim = animRef.current;
    if (anim.frameRunning) return;
    anim.frameRunning = true;
    anim.lastFrameTime = performance.now();
    requestAnimationFrame(frame);
  }, [frame]);

  // 初始化
  useEffect(() => {
    measure();
    startFrame();

    const handleScroll = () => startFrame();
    const handleResize = () => {
      measure();
      startFrame();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [measure, startFrame]);

  return (
    <section className="orbit-sec" id="orbitSec" ref={sectionRef} aria-labelledby="orbitTitle">
      <div className="orbit-sticky" id="orbitSticky" ref={stickyRef}>
        <header className="orbit-head">
          <div className="orbit-kicker">WHY NUWORK · 06</div>
          <h2 id="orbitTitle">
            让一次创造，<br/><em>持续成为你的数字资产。</em>
          </h2>
          <p>从成果发布到客户服务，从公网访问到工程分享——让每一份 AI 产出持续工作。</p>
        </header>

        {/* 光晕 */}
        <div className="orbit-halo" ref={haloRef} aria-hidden="true"></div>

        {/* 中心 App 砖 - V2 浅色液态玻璃圆角方形 196x196px border-radius:58px */}
        <div className="orbit-app" ref={appRef} aria-label="Nuwork 协同核心">
          <div className="orbit-app-orb">
            <span className="core-mark">Nu</span>
            <span className="core-name">非凡工作</span>
          </div>
        </div>

        {/* 卫星网络 - V2 液态玻璃风格卡片 */}
        <div className="orbit-network" role="list" aria-label="六种 AI 成果复利">
          {SATS.map((sat, i) => (
            <article 
              key={i}
              className={`sat sat${i + 1}`}
              role="listitem"
              ref={el => satsRef.current[i] = el}
            >
              <span 
                className="sat-orb" 
                aria-hidden="true"
                ref={el => orbsRef.current[i] = el}
              >
                <b>{sat.metric}</b>
              </span>
              <span 
                className="sat-label"
                ref={el => labelsRef.current[i] = el}
              >
                <small>{sat.num} · {sat.tag}</small>
                <strong>{sat.title}</strong>
                <em>{sat.desc}</em>
              </span>
            </article>
          ))}
        </div>

        {/* CTA */}
        <a 
          className="orbit-cta" 
          href={config.market.home}
          target="_blank" 
          rel="noopener noreferrer"
          ref={ctaRef}
        >
          探索应用市场 <span aria-hidden="true">↗</span>
        </a>

        {/* 滚动提示 */}
        <div className="orbit-scroll-hint" ref={hintRef} aria-hidden="true">
          向下滚动 · 看见六种成果复利
        </div>
      </div>
    </section>
  );
}

export default Orbit;
